import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { AutoComplete } from 'primereact/autocomplete';
import { debounce } from '../../utils/debounce';
import { searchCompanyApi } from '../../redux/apis/company';
import Label from '../Label';

const AUTOCOMPLETE_DEBOUNCE_MS = 250;

const CompanyAutocomplete = ({
  name = 'currentCompany',
  formik,
  value,
  onChange,
  onBlur,
  onFocus,
  labelText,
  placeholder = 'Select or type Current Company',
  isMandatory = false,
  className = '',
  inputClassName = '',
  disabled = false
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const cacheRef = useRef({});
  const autoCompleteRef = useRef(null);
  const requestIdRef = useRef(0);

  // Extract external string value
  const externalValue = useMemo(() => {
    if (formik && formik.values) {
      return formik.values[name] || '';
    }
    return value || '';
  }, [formik, name, value]);

  const [inputText, setInputText] = useState(externalValue || '');

  // Keep internal text state aligned with external prop changes
  useEffect(() => {
    setInputText(externalValue || '');
  }, [externalValue]);

  // Debounced API search with query caching & request tracking
  const debouncedSearch = useMemo(
    () =>
      debounce(async (query, reqId) => {
        const trimmed = (query || '').trim();
        if (!trimmed) {
          if (reqId === requestIdRef.current) {
            setSuggestions([]);
          }
          return;
        }

        const cacheKey = trimmed;
        if (cacheRef.current[cacheKey]) {
          if (reqId === requestIdRef.current) {
            setSuggestions([...cacheRef.current[cacheKey]]);
          }
          return;
        }

        try {
          const res = await searchCompanyApi(trimmed);
          const items = res?.data?.data || res?.data || [];
          cacheRef.current[cacheKey] = items;
          if (reqId === requestIdRef.current) {
            setSuggestions([...items]);
          }
        } catch (err) {
          console.error('Company autocomplete error:', err);
          if (reqId === requestIdRef.current) {
            setSuggestions([]);
          }
        }
      }, AUTOCOMPLETE_DEBOUNCE_MS),
    []
  );

  // Clean up debounce on unmount
  useEffect(() => {
    return () => {
      debouncedSearch?.cancel?.();
    };
  }, [debouncedSearch]);

  const handleComplete = useCallback(
    (e) => {
      const query = (e.query || '').trim();
      const currentReqId = ++requestIdRef.current;

      if (!query) {
        debouncedSearch?.cancel?.();
        setSuggestions([]);
        return;
      }

      const cacheKey = query;
      if (cacheRef.current[cacheKey]) {
        debouncedSearch?.cancel?.();
        setSuggestions([...cacheRef.current[cacheKey]]);
        return;
      }

      debouncedSearch(query, currentReqId);
    },
    [debouncedSearch]
  );

  const handleInputChange = (e) => {
    const rawVal = e.value;
    const stringVal = typeof rawVal === 'string' ? rawVal : rawVal?.name || '';
    setInputText(stringVal);

    if (!stringVal || stringVal.trim().length === 0) {
      debouncedSearch?.cancel?.();
      requestIdRef.current++;
      setSuggestions([]);
    }

    if (formik) {
      formik.setFieldValue(name, stringVal);
      if (typeof rawVal === 'object' && (rawVal?.id || rawVal?.ID)) {
        formik.setFieldValue('companyId', rawVal.id || rawVal.ID);
      } else {
        formik.setFieldValue('companyId', null);
      }
    }

    if (onChange) {
      onChange({ target: { name, value: stringVal }, value: stringVal });
    }
  };

  const handleSelect = (e) => {
    const selected = e.value;
    const finalName = typeof selected === 'string' ? selected : selected?.name || '';
    const compId = typeof selected === 'object' ? selected?.id || selected?.ID : null;

    // Invalidate pending requests, cancel debounce, and clear suggestions to close dropdown
    debouncedSearch?.cancel?.();
    requestIdRef.current++;
    setSuggestions([]);

    if (autoCompleteRef.current?.hideOverlay) {
      autoCompleteRef.current.hideOverlay();
    }

    setInputText(finalName);

    if (formik) {
      formik.setFieldValue(name, finalName);
      if (compId) {
        formik.setFieldValue('companyId', compId);
      }
    }

    if (onChange) {
      onChange({ target: { name, value: finalName }, value: finalName });
    }
  };

  const handleBlur = (e) => {
    if (formik) {
      formik.setFieldTouched(name, true);
    }
    if (onBlur) {
      onBlur(e);
    }
  };

  const handleFocus = (e) => {
    if (onFocus) {
      onFocus(e);
    }
  };

  const isError = Boolean(formik && formik.touched[name] && formik.errors[name]);

  return (
    <div className={`rpt-autocomplete-wrapper ${className}`}>
      {labelText && <Label htmlFor={name} text={labelText} isMandatory={isMandatory} />}
      <AutoComplete
        ref={autoCompleteRef}
        id={name}
        name={name}
        value={inputText}
        suggestions={suggestions}
        completeMethod={handleComplete}
        field="name"
        placeholder={placeholder}
        onChange={handleInputChange}
        onSelect={handleSelect}
        onFocus={handleFocus}
        onBlur={handleBlur}
        disabled={disabled}
        className={`w-full ${isError ? 'p-invalid' : ''}`}
        inputClassName={`w-full ${inputClassName}`.trim()}
        itemTemplate={(item) => (
          <div className="rpt-autocomplete-item">
            <span className="rpt-autocomplete-item__primary">{item.name}</span>
          </div>
        )}
      />
      {isError && <small className="p-error block mt-1">{formik.errors[name]}</small>}
    </div>
  );
};

export default CompanyAutocomplete;
